import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { CATEGORIES, ZONES, UNIT_LABELS } from "@/lib/constants";
import { Camera, Upload, ImagePlus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import MedicineSelector from "@/components/MedicineSelector";

const PHOTO_FIELDS = [
  { key: "photo_front",      label: "Frente de la caja", hint: "Que se vea claramente el nombre" },
  { key: "photo_back",       label: "Parte posterior",   hint: "Información trasera del empaque" },
  { key: "photo_expiration", label: "Fecha de caducidad",hint: "Donde se vea la fecha de vencimiento" },
  { key: "photo_contents",   label: "Contenido real",    hint: "Pastillas o contenido dentro de la caja" },
];

function PhotoSlot({ field, value, onChange }) {
  const inputRef = useRef(null);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("La foto debe pesar menos de 4 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col">
      <Label className="text-sm font-medium text-slate-800 mb-1.5">{field.label}</Label>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`relative h-40 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center text-center px-3 transition-colors btn-press ${
          value
            ? "border-blue-400 bg-blue-50"
            : "border-blue-300 bg-blue-50/40 hover:bg-blue-50"
        }`}
        data-testid={`photo-upload-${field.key}`}
      >
        {value ? (
          <>
            <img src={value} alt={field.label} className="absolute inset-0 w-full h-full object-cover rounded-2xl" />
            <div className="absolute top-2 right-2 flex gap-1">
              <span className="bg-white/90 rounded-full p-1.5 shadow-sm">
                <Camera className="w-4 h-4 text-blue-700" />
              </span>
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="bg-white/90 rounded-full p-1.5 shadow-sm cursor-pointer"
                data-testid={`photo-remove-${field.key}`}
              >
                <Trash2 className="w-4 h-4 text-rose-600" />
              </span>
            </div>
          </>
        ) : (
          <>
            <ImagePlus className="w-8 h-8 text-blue-600" />
            <span className="mt-2 text-sm font-medium text-blue-800">Tocar para subir</span>
            <span className="text-[11px] text-slate-500 mt-0.5">{field.hint}</span>
          </>
        )}
      </button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" />
    </div>
  );
}

export default function PublishPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [catalog, setCatalog] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [form, setForm] = useState({
    medicine_name: "",
    category: "",
    total_units: "",
    unit_label: "tabletas",
    base_cost: "",
    delivery_cost: "0",
    whatsapp: "",
    zone: "",
    description: "",
    expiration_date: "",
    photo_front: "",
    photo_back: "",
    photo_expiration: "",
    photo_contents: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/listings/${editId}`)
      .then((r) => {
        const d = r.data;
        setForm({
          medicine_name: d.medicine_name || "",
          category: d.category || "",
          total_units: String(d.total_units || ""),
          unit_label: d.unit_label || "tabletas",
          base_cost: String(d.base_cost ?? ""),
          delivery_cost: String(d.delivery_cost ?? "0"),
          whatsapp: d.whatsapp || "",
          zone: d.zone || "",
          description: d.description || "",
          expiration_date: d.expiration_date || "",
          photo_front: d.photo_front || "",
          photo_back: d.photo_back || "",
          photo_expiration: d.photo_expiration || "",
          photo_contents: d.photo_contents || "",
        });
      })
      .catch(() => toast.error("No se pudo cargar la publicación"))
      .finally(() => setLoadingExisting(false));
  }, [isEdit, editId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    // Validate photos
    for (const p of PHOTO_FIELDS) {
      if (!form[p.key]) {
        toast.error(`Falta la foto: ${p.label}`);
        return;
      }
    }
    if (!form.category) return toast.error("Selecciona la categoría");
    if (!form.zone) return toast.error("Selecciona la zona de la ciudad");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        total_units: parseInt(form.total_units, 10),
        base_cost: parseFloat(form.base_cost),
        delivery_cost: parseFloat(form.delivery_cost || "0"),
      };
      if (isEdit) {
        await api.put(`/listings/${editId}`, payload);
        toast.success("Publicación actualizada. Volverá a revisión.");
      } else {
        await api.post("/listings", payload);
        toast.success("Publicación enviada. El administrador la revisará pronto.");
      }
      navigate("/mis-publicaciones");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo publicar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">
            {isEdit ? "Editar publicación" : "Ofrecer un medicamento"}
          </h1>
          <p className="mt-2 text-slate-600 text-base">
            {isEdit
              ? "Al guardar cambios, tu publicación volverá a revisión por el administrador."
              : "Sube 4 fotos claras y los datos básicos. Tu publicación pasará por una breve revisión."}
          </p>
        </div>

        {loadingExisting ? (
          <div className="text-center text-slate-500 py-12">Cargando publicación…</div>
        ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Photos */}
          <Card className="p-5 sm:p-6 rounded-2xl border border-slate-200">
            <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Camera className="w-5 h-5 text-blue-600"/>Fotos obligatorias</h2>
            <p className="text-sm text-slate-500 mt-1">Las 4 fotos ayudan a generar confianza con quien compra.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {PHOTO_FIELDS.map((f) => (
                <PhotoSlot key={f.key} field={f} value={form[f.key]} onChange={(v) => setField(f.key, v)} />
              ))}
            </div>
          </Card>

          {/* Info */}
          <Card className="p-5 sm:p-6 rounded-2xl border border-slate-200 space-y-4">
            <h2 className="font-display text-lg font-semibold text-slate-900">Información del medicamento</h2>

            <div>
              <Label className="text-sm font-medium text-slate-800">Medicamento</Label>
              <MedicineSelector
                value={form.medicine_name}
                category={form.category}
                onSelect={({ medicine_name, category }) => {
                  setForm((f) => ({
                    ...f,
                    medicine_name,
                    category: category || f.category,
                  }));
                }}
              />
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                <Info className="w-3 h-3" /> Toca para elegir del catálogo y evitar errores. Si no aparece, puedes escribirlo a mano.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="publish-category-select">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Caducidad (opcional)</Label>
                <Input type="date" value={form.expiration_date} onChange={(e) => setField("expiration_date", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50" data-testid="publish-expiration-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Total disponible</Label>
                <Input type="number" min={1} required value={form.total_units} onChange={(e)=>setField("total_units", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50" placeholder="20" data-testid="publish-units-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Unidad</Label>
                <Select value={form.unit_label} onValueChange={(v) => setField("unit_label", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="publish-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_LABELS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Costo base (MXN)</Label>
                <Input type="number" min={0} step="0.01" required value={form.base_cost} onChange={(e)=>setField("base_cost", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50" placeholder="120" data-testid="publish-base-cost-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Costo de entrega a domicilio</Label>
                <Input type="number" min={0} step="0.01" value={form.delivery_cost} onChange={(e)=>setField("delivery_cost", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50" placeholder="0 si entregas en mano" data-testid="publish-delivery-cost-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Zona de la ciudad</Label>
                <Select value={form.zone} onValueChange={(v) => setField("zone", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="publish-zone-select">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-800">WhatsApp (con código de país)</Label>
              <Input required value={form.whatsapp} onChange={(e)=>setField("whatsapp", e.target.value.replace(/[^0-9+]/g, ""))} className="h-12 mt-1.5 text-base bg-slate-50" placeholder="+52 55 1234 5678" data-testid="publish-whatsapp-input" />
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><Info className="w-3 h-3"/>Se usará para que las personas te contacten directamente.</p>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-800">Notas adicionales (opcional)</Label>
              <Textarea rows={3} value={form.description} onChange={(e)=>setField("description", e.target.value)} className="mt-1.5 text-base bg-slate-50" placeholder="Caja sellada, sólo abrí para confirmar la fecha de caducidad…" data-testid="publish-description-textarea" />
            </div>
          </Card>

          <Button type="submit" disabled={submitting} className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700 rounded-xl btn-press shadow-md" data-testid="publish-submit-btn">
            <Upload className="w-5 h-5 mr-2" />
            {submitting ? "Enviando…" : isEdit ? "Guardar cambios" : "Publicar medicamento"}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
}
